import { useContext, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { FormInput, Wrapper } from 'components'
import { AuthContext } from 'contexts/AuthContext'
import { Rocket } from 'components/Icons/rocket'
import { downloadPDF } from 'utils/downloadPdf'
import {
  Button,
  Card,
  Modal,
  ModalContent,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  toast,
} from 'keep-react'
import { ArrowLeft, DownloadSimple, MapPin } from 'phosphor-react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { PolicyListItem, PolicyListResponse } from 'api/types/policies'
import { policiesService } from 'api/services/policies'
import { RouteNamesAuth } from 'routes/enum'
import { docService } from 'api/services/doc'
import { formatCurrency } from 'utils/currency'
import { renderBadge } from 'utils/renderBadge'
import { renderPolicyStatus } from 'utils/renderPolicyStatus'
import { isoCountries } from 'utils/countries'
import { AppContext } from 'contexts/AppContext'

const validationSchema = Yup.object().shape({
  ADDRESS1: Yup.string().required('Obrigatório'),
  ADDRESS3: Yup.string().required('Obrigatório'),
  ADDRESS4: Yup.string(),
  POSTCODE: Yup.string()
    .required('Obrigatório')
    .matches(/^\d{4}-\d{3}$/, 'Código Postal deve ser no formato ####-###'),
})

type InitialValues = {
  ADDRESS1?: string
  ADDRESS2?: string
  ADDRESS3?: string
  ADDRESS4?: string
  POSTCODE?: string
  COUNTRY?: string
}

export const Policy = () => {
  const { policyNo } = useParams() as unknown as { policyNo: number }
  const [policy, setPolicy] = useState<PolicyListItem | null>(null)
  const navigate = useNavigate()
  const { currentBroker } = useContext(AuthContext)
  const { setIsCallingApi } = useContext(AppContext)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [changeAddress, setChangeAddress] = useState(false)
  const [initialValues, setInitialValues] = useState<InitialValues>({
    ADDRESS1: '',
    ADDRESS2: '',
    ADDRESS3: '',
    ADDRESS4: '',
    POSTCODE: '',
    COUNTRY: '',
  })
  const brokerNo = currentBroker?.BrokerNo ? currentBroker?.BrokerNo : 0

  //get policy details
  useEffect(() => {
    const fetchPolicy = async () => {
      if (!policyNo || brokerNo === 0) return false

      try {
        const response = (await policiesService.getPoliciesList({
          policyNo: Number(policyNo),
          brokerNo,
          page: 0,
        })) as PolicyListResponse
        if (Array.isArray(response.Policies)) {
          setPolicy(response.Policies[0])
          setInitialValues({
            ADDRESS1: response.Policies[0]?.AddressLine1 || '',
            ADDRESS2: response.Policies[0]?.AddressLine2 || '',
            ADDRESS3: response.Policies[0]?.AddressLine3 || '',
            ADDRESS4: response.Policies[0]?.AddressLine4 || '',
            POSTCODE: response.Policies[0]?.Postcode || '',
            COUNTRY:
              isoCountries.find(
                country =>
                  country.code ===
                  response.Policies[0]?.Country.toLocaleUpperCase(),
              )?.country ||
              response.Policies[0]?.Country ||
              '',
          })

          setChangeAddress(false)
          setIsLoading(false)
        } else {
          setPolicy(null)
          console.error('Policy error:', response)
        }
      } catch (error) {
        // setTableData(null)
      }
    }
    fetchPolicy()
  }, [policyNo, brokerNo])

  // download receipt document
  const getReceipt = async () => {
    setIsCallingApi(true)
    try {
      const response = await docService.getDocumentList(policyNo, 0, 'I')
      if (!response.Documents || response.Documents.length === 0) {
        toast.error('Nenhum documento encontrado')
        return
      }
      downloadPDF({
        base64Data: response.Documents[0].Document,
        filename: `Aviso de Recibo - ${policyNo}.pdf`,
      })
    } catch (error) {
      console.error(error)
    } finally {
      setIsCallingApi(false)
    }
  }

  // download invoice document
  const getInvoice = async () => {
    setIsCallingApi(true)

    try {
      const response = await docService.getDocumentList(policyNo, 0, 'R')
      if (!response.Documents || response.Documents.length === 0) {
        toast.error('Nenhum documento encontrado')
        return
      }
      downloadPDF({
        base64Data: response.Documents[0].Document,
        filename: `Factura Recibo - ${policyNo}.pdf`,
      })
    } catch (error) {
      console.error(error)
    } finally {
      setIsCallingApi(false)
    }
  }

  // download private conditions document
  const getPrivateConditions = async () => {
    setIsCallingApi(true)
    try {
      const response = await docService.getDocumentList(policyNo, 0)
      if (!response.Documents || response.Documents.length === 0) {
        toast.error('Nenhum documento encontrado')
        return
      }
      downloadPDF({
        base64Data: response.Documents[0].Document,
        filename: `Condicoes Particulares - ${policyNo}.pdf`,
      })
    } catch (error) {
      console.error(error)
    } finally {
      setIsCallingApi(false)
    }
  }

  // toggle address modal
  const updateAddress = () => {
    setChangeAddress(!changeAddress)
  }

  // define action buttons
  const buttonArea = [
    {
      label: 'Aviso de Recibo',
      action: () => getReceipt(),
      icon: <DownloadSimple size={16} />,
    },
    {
      label: 'Factura Recibo',
      action: () => getInvoice(),
      icon: <DownloadSimple size={16} />,
    },
    // {
    //   label: 'Condições Gerais/Especiais',
    //   action: () => getGeneralConditions(),
    //   icon: <DownloadSimple size={16} />,
    // },
    {
      // label: 'Condições Particulares',
      label: 'Condições Contratuais',
      action: () => getPrivateConditions(),
      icon: <DownloadSimple size={16} />,
    },
    {
      label: 'Alterar morada de correspondência',
      action: () => updateAddress(),
      icon: <MapPin size={16} />,
    },
  ]

  // formik for address change
  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: false,
    validationSchema,
    onSubmit: async values => {
      const updateAddressBody = {
        policyAddress: Object.entries(values)
          .map(([k, v]) => ({
            question: k,
            answer: v,
            inputId: k,
          }))
          .map(item => {
            if (item.question === 'COUNTRY') {
              return {
                ...item,
                answer:
                  isoCountries.find(
                    country =>
                      country.country === item.answer ||
                      country.code === item.answer ||
                      country.code === item.answer,
                  )?.code || item.answer,
              }
            }
            return item
          }),
      }

      setIsCallingApi(true)
      try {
        const response = (await policiesService.updatePolicyAddress({
          policyNo,
          body: updateAddressBody,
        })) as PolicyListResponse
        setPolicy(response.Policies[0])
        setInitialValues({
          ADDRESS1: response.Policies[0]?.AddressLine1 || '',
          ADDRESS2: response.Policies[0]?.AddressLine2 || '',
          ADDRESS3: response.Policies[0]?.AddressLine3 || '',
          ADDRESS4: response.Policies[0]?.AddressLine4 || '',
          POSTCODE: response.Policies[0]?.Postcode || '',
          COUNTRY:
            isoCountries.find(
              country =>
                country.code ===
                response.Policies[0]?.Country.toLocaleUpperCase(),
            )?.country ||
            response.Policies[0]?.Country ||
            '',
        })
        setChangeAddress(false)
      } catch (error) {
        console.error(error)
      } finally {
        setIsCallingApi(false)
      }
    },
  })

  if (!policy) {
    return <Spinner />
  }

  return (
    <div className="w-full flex-1 py-12 overflow-auto">
      <Wrapper>
        <Button
          variant="outline"
          color="secondary"
          className="mb-6"
          onClick={() => navigate(RouteNamesAuth.POLICIES)}
        >
          <ArrowLeft size={16} /> Apólices
        </Button>
        <h2 className="uppercase text-sm font-medium text-metal-900">
          Consulte a apólice gravada
        </h2>
        <h1 className="pb-10 text-xxxl font-medium text-primary-600">
          Apólice Nº {policyNo}
        </h1>

        {isLoading ? (
          <Card className="p-4 w-full max-w-full">
            <Spinner color="primary" className="mx-auto" />
          </Card>
        ) : !policy ? (
          <Card className="p-4 w-full max-w-full">
            <div className="text-heading-6 text-metal-400">
              Apólice indisponível
            </div>
          </Card>
        ) : (
          <div className="grid grid-col-1 lg:grid-cols-[3fr_1fr] gap-4 w-full">
            <Card className="p-4 w-full max-w-full flex-shrink-0">
              <div className="flex items-start gap-2 w-full">
                <div className="flex items-center flex-col gap-1 pt-3 -translate-y-1.5">
                  <Rocket />
                </div>
                <h2 className="text-body-1 uppercase pb-10">
                  {(policy && policy['Produto']) || 'Produto indisponível'}
                </h2>
              </div>
              <Table className="pb-10">
                <TableBody>
                  {policy &&
                    Object.entries(policy).map((item: any, index: number) => {
                      const rejectsKeys = [
                        'LastEvent',
                        'AddressLine1',
                        'AddressLine2',
                        'AddressLine3',
                        'AddressLine4',
                        'Postcode',
                        'Country',
                        'ModifiedDate',
                        'Capital Seguro',
                        'CodigoProduto',
                      ]
                      if (rejectsKeys.includes(item[0])) return null
                      if (item['0'] === 'Razão' && item['1'] === '') return null
                      return (
                        <TableRow key={`row-${index}`}>
                          <TableHead>
                            {item[0] === 'Branch' ? 'Mediador' : item[0]}
                          </TableHead>
                          <TableCell className="normal-case">
                            {item[0] === 'Capital Seguro' ||
                            item[0] === 'Prémio Anual'
                              ? formatCurrency(item[1])
                              : item[0] === 'Estado'
                                ? renderBadge(item[1])
                                : item[0] === 'Razão'
                                  ? renderPolicyStatus(item[1])
                                  : item[0] === 'Forma de Pagamento' &&
                                      item[1] === 'Direct Debit'
                                    ? 'Débito Directo'
                                    : item[0] === 'Forma de Pagamento' &&
                                        item[1] === 'Aviso de Recibo'
                                      ? 'Multibanco'
                                      : item[1]}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                </TableBody>
              </Table>
            </Card>
            <div className="flex-grow-0">
              <Card className="p-4">
                <nav className="flex flex-col items-start justify-start gap-4">
                  {buttonArea.map((item, index) => (
                    <Button
                      key={`button-${index}`}
                      color="secondary"
                      variant="softBg"
                      className="w-full gap-2 whitespace-normal items-center justify-start text-left h-auto py-1.5"
                      onClick={item.action}
                    >
                      {item.icon}
                      {item.label}
                    </Button>
                  ))}
                </nav>
              </Card>
            </div>
          </div>
        )}

        {
          <Modal open={changeAddress} onOpenChange={setChangeAddress}>
            <ModalContent className="max-w-lg p4">
              <h2 className="pb-10 text-xxxl font-medium text-primary-600">
                Mudar morada de cliente
              </h2>
              <form onSubmit={formik.handleSubmit}>
                <FormInput
                  formik={formik}
                  label="Morada"
                  name="ADDRESS1"
                  type="text"
                  required
                />
                <FormInput
                  formik={formik}
                  label="Morada (cont.)"
                  name="ADDRESS2"
                  type="text"
                />
                <FormInput
                  formik={formik}
                  label="Localidade"
                  name="ADDRESS3"
                  type="text"
                  required
                />
                <FormInput
                  formik={formik}
                  label="Código Postal"
                  name="POSTCODE"
                  type="text"
                />
                <FormInput
                  formik={formik}
                  label="País"
                  name="COUNTRY"
                  type="text"
                />
                <h3 className="bg-black/20 rounded-xl p-4 text-primary-500 text-md font-medium mt-4 mb-2">
                  NOTA: A alteração da morada de correspondência será aplicada a
                  todas as moradas associadas a este NIF. A morada de risco
                  permanece inalterada.
                </h3>
                <nav className="flex gap-4 mt-6">
                  <Button
                    variant="outline"
                    color="secondary"
                    className="w-1/2"
                    onClick={() => setChangeAddress(false)}
                  >
                    Cancelar
                  </Button>
                  <Button type="submit" className="w-1/2">
                    Gravar alterações
                  </Button>
                </nav>
              </form>
            </ModalContent>
          </Modal>
        }
      </Wrapper>
    </div>
  )
}
